import React, { useState, useEffect } from "react";
import { Printer, ChevronLeft, CreditCard, X, QrCode, CheckCircle2, Hash, IndianRupee } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { db } from "../../firebase";
import { ref, set, get } from "firebase/database";

const WorkSelectionChallan = () => {
    const navigate = useNavigate();
    const [selectedWorks, setSelectedWorks] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);
    
    // Payment States
    const [showPayment, setShowPayment] = useState(false);
    const [loading, setLoading] = useState(false);
    const [paymentDone, setPaymentDone] = useState(false);
    const [showInvoice, setShowInvoice] = useState(false);
    const [invoiceData, setInvoiceData] = useState(null);
    const [paymentData, setPaymentData] = useState({
        userUpiId: "",
        paidAmount: "250"
    });
    const [isPaid, setIsPaid] = useState(false);

    useEffect(() => {
        const works = JSON.parse(localStorage.getItem('selectedWorksForChallan')) || [];
        const user = JSON.parse(localStorage.getItem('currentUser')) || {};
        setSelectedWorks(works);
        setCurrentUser(user);

        // Check if already paid for these works
        const fetchPaymentStatus = async () => {
            if (user._node && user._key && works.length > 0) {
                const meetingNum = works[0]?.meetingNumber || "N/A";
                try {
                    const paymentRef = ref(db, `${user._node}/${user._key}/work_payment/${meetingNum}`);
                    const snapshot = await get(paymentRef);
                    if (snapshot.exists()) {
                        setIsPaid(true);
                        setInvoiceData(snapshot.val());
                    }
                } catch (error) {
                    console.error("Error fetching payment status:", error);
                }
            }
        };
        fetchPaymentStatus();
    }, []);

    const handlePrint = () => {
        window.print();
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setPaymentData(prev => ({ ...prev, [name]: value }));
    };

    const handleCompletePayment = async () => {
        if (!paymentData.userUpiId) {
            alert("कृपया तुमचा यूपीआय आयडी भरा (Please fill your UPI ID)");
            return;
        }

        setLoading(true);
        try {
            const userKey = currentUser._key;
            const userNode = currentUser._node;
            const meetingNum = selectedWorks[0]?.meetingNumber || "N/A";
            
            const paymentRef = ref(db, `${userNode}/${userKey}/work_payment/${meetingNum}`);
            
            const receiptId = `ZP-WORK-${Math.floor(100000 + Math.random() * 900000)}`;
            const timestamp = new Date().toISOString();

            const paymentPayload = {
                userUpiId: paymentData.userUpiId,
                paidAmount: paymentData.paidAmount,
                userName,
                mobile,
                date: timestamp,
                receiptId: receiptId,
                status: 'completed',
                type: 'Work Selection Fee',
                meetingNumber: meetingNum, 
                selectedWorks: selectedWorks.map(w => `${w.workNumber} - ${w.schemeName || ''}`)
            };

            await set(paymentRef, paymentPayload);
            
            setPaymentDone(true);
            setIsPaid(true);
            setInvoiceData(paymentPayload);

            setTimeout(() => {
                setPaymentDone(false);
                setShowPayment(false);
                setShowInvoice(true);
            }, 2000);

        } catch (error) {
            alert("पेमेंट सबमिट करताना त्रुटी आली: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    const today = new Date().toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });

    const challanNo = `ZP${new Date().getFullYear()}${Math.floor(100000 + Math.random() * 900000)}`;
    const upiId = "sangramshinde7007@okhdfcbank";
    const amount = 250;
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(`upi://pay?pa=${upiId}&pn=Zilla%20Parishad%20Sangli&am=${amount}&cu=INR&tn=Work%20Selection%20Fee`)}`;

    const isSociety = currentUser?._node?.includes('Society');
    const userName = isSociety 
        ? (currentUser?.societyName || "संस्थेचे नाव")
        : `${currentUser?.surname || ''} ${currentUser?.firstName || ''} ${currentUser?.middleName || ''}`.trim() || "वापरकर्ता";
    
    const mobile = isSociety 
        ? (currentUser?.chairmanWhatsapp || "9130516001")
        : (currentUser?.whatsappNo || "9130516001");

    const address = isSociety 
        ? (currentUser?.societyAddress || "Samdoli")
        : (currentUser?.currentAddress || "Samdoli");

    const workNumbersForPrint = selectedWorks.map(w => w.workNumber).join(", ");
    const workNumbersWithScheme = selectedWorks.map(w => `${w.workNumber} (${w.schemeName || ''})`).join(", ");

    return (
        <div className="min-h-screen bg-gray-100 p-4 md:p-8 print:p-0 print:bg-white transition-all duration-500">
            {/* Action Bar - Hidden on Print */}
            <div className="max-w-5xl mx-auto mb-8 flex justify-between items-center print:hidden">
                <button 
                    onClick={() => navigate('/worklist')}
                    className="flex items-center gap-2 px-6 py-3 bg-white text-gray-700 rounded-2xl font-bold shadow-sm hover:bg-gray-50 transition-all border border-gray-200"
                >
                    <X size={20} />
                    मागे फिरा
                </button>
                <div className="flex gap-4">
                    <button 
                        onClick={handlePrint}
                        className="flex items-center gap-2 px-8 py-3 bg-gray-50 text-gray-700 rounded-2xl font-bold shadow-sm hover:bg-gray-100 transition-all border border-gray-200"
                    >
                        <Printer size={20} />
                        चलन प्रिंट करा
                    </button>
                </div>
            </div>

            {/* Challan Container */}
            <div className="max-w-[1000px] mx-auto bg-white shadow-2xl p-0 md:p-12 print:shadow-none print:p-0 overflow-x-auto font-sans relative">
                {/* Background Decoration */}
                <div className="absolute top-0 right-0 -mt-20 -mr-20 w-96 h-96 bg-blue-50/20 rounded-full blur-3xl -z-10"></div>
                
                <div className="min-w-[800px] border-[1.5px] border-black text-[12px] leading-tight text-black bg-white">
                    
                    {/* Row 1: Form No and Chalan No */}
                    <div className="grid grid-cols-2 border-b border-black">
                        <div className="p-3 border-r border-black font-bold text-[14px]">Form No.2</div>
                        <div className="p-3 font-bold text-right text-[15px] uppercase tracking-tight">CHALAN NO - {challanNo}</div>
                    </div>

                    {/* Row 2: Bank Info */}
                    <div className="p-3 border-b border-black text-center md:text-left">
                        <p className="font-bold text-[13px] mb-1">Chalan of Cash paid into the Sangli Dist. Central Co-Op. Bank Ltd. at</p>
                        <p className="font-bold text-[13px]">सांगली जिल्हा मध्यवर्ती सहकारी बँक लि. मध्ये भरलेल्या रोख रकमेचे चलन</p>
                    </div>

                    {/* Row 3: Account Info */}
                    <div className="p-3 border-b border-black font-bold text-[13px]">
                        for creadit to the Zilla Parishad सांगली A/C - १०५०५३४००००७७३ with the Head Office
                    </div>

                    {/* Row 4: Column Headers - Exactly like image */}
                    <div className="grid grid-cols-[1fr_1fr] border-b border-black font-bold text-center">
                        <div className="p-2 border-r border-black">
                            <p className="mb-1 text-[13px]">To be filled in by the remitter</p>
                            <p className="text-[13px]">रक्कम पाठविणाऱ्याने भरावयाचे</p>
                        </div>
                        <div className="p-2">
                            <p className="mb-1 text-[13px]">To be filled in by the Dep.Off</p>
                            <p className="text-[13px]">खात्याच्या अंमलदाराने भरावयाचे</p>
                        </div>
                    </div>

                    {/* Row 5: Main Content Grid */}
                    <div className="grid grid-cols-[3fr_1.5fr_2.5fr]">
                        {/* Name and Address */}
                        <div className="border-r border-black flex flex-col">
                            <div className="p-3 flex-grow min-h-[140px] space-y-4">
                                <div>
                                    <p className="font-bold text-[13px]">By whom tendered (Name)</p>
                                    <p className="font-medium">रक्कम कोणी आणून दिली त्याचे नांव</p>
                                </div>
                                <div>
                                    <p className="font-bold text-[13px]">Name (or designation ) and address of the person on whose behalf money is paid</p>
                                    <p className="font-medium">ज्यांच्या वतीने रक्कम भरण्यात आली त्या मनुष्याचे नांव (किंवा हुद्दा ) व पत्ता</p>
                                </div>
                            </div>
                        </div>

                        {/* User Dynamic Data - Center Bold Box */}
                        <div className="border-r border-black p-4 flex flex-col justify-center text-center font-bold bg-gray-50/10">
                            <p className="text-[15px] mb-2 uppercase leading-tight">{userName}</p>
                            <p className="text-[15px] mb-2">{mobile}</p>
                            <p className="text-[14px] leading-snug">{address}</p>
                        </div>

                        {/* Account Classification */}
                        <div className="p-4 space-y-4">
                            <div className="space-y-1">
                                <p className="font-bold text-[13px]">Account Classification</p>
                                <p className="font-bold whitespace-nowrap">Major Head - 0059 <span className="font-medium">सार्वजनिक मालमत्तेपासून उत्पन्न</span></p>
                                <p className="font-bold whitespace-nowrap">Minor Head - 800 <span className="font-medium">इतर जमा</span></p>
                                <p className="font-bold whitespace-nowrap">Detailed Head - <span className="font-medium text-[13px]">निविदा फॉर्म फी</span></p>
                            </div>
                            
                            <div className="grid grid-cols-2 mt-8 font-bold">
                                <div>
                                    <p className="underline underline-offset-4">Correct</p>
                                    <p className="mt-2 text-[12px]">बरोबर</p>
                                </div>
                                <div className="text-right">
                                    <p>Recived and grant receipts</p>
                                    <p className="mt-2 text-[12px]">पैसे घेऊन पावत्या द्याव्यात.</p>
                                </div>
                            </div>

                            <p className="font-bold mt-6 text-[13px]">दिनांक : {today}</p>
                        </div>
                    </div>

                    {/* Row 6: Particulars and Amount Section */}
                    <div className="grid grid-cols-[3fr_1.5fr_2.5fr] border-t border-black">
                        <div className="p-3 border-r border-black min-h-[100px] space-y-4">
                            <p className="font-bold text-[13px]">Full perticulars of the remittances and the authority (if any)</p>
                            <p className="font-medium leading-normal">पाठवलेल्या रकमेचा व अधिकारपत्र (कोणतेही असल्यास ) त्याचा पूर्ण तपशील</p>
                        </div>
                        <div className="border-r border-black flex flex-col items-center justify-center p-2 font-bold bg-gray-50/20">
                            <p className="text-[14px]">Amount</p>
                            <p className="text-[14px]">रक्कम</p>
                        </div>
                        <div className="p-4 font-bold space-y-2">
                            <p className="text-[13px]">उप अभियंता (इवड), पंचायत समिती...</p>
                            <p className="text-[13px]">बांधकाम विभाग जि . प . सांगली</p>
                            <p className="text-[13px]">पैशाचा भरणा करणाऱ्याबद्दल फर्मविणाऱ्या अंमलदाराची सही व पुर्ण हुद्दा</p>
                        </div>
                    </div>

                    {/* Row 7: Work Numbers Row */}
                    <div className="grid grid-cols-[3fr_1.5fr_2.5fr] border-t border-black">
                        <div className="p-4 border-r border-black font-bold text-[13px]">
                            निविदा सूचना क्र . ०१ / २०२५-२६ काम क्र - <span className="text-[14px]">{workNumbersForPrint || "____, ____, ____"}</span>
                        </div>
                        <div className="border-r border-black flex items-center justify-center font-bold text-[18px]">
                            250 /-
                        </div>
                        <div className="p-4 space-y-4">
                            <p className="font-bold leading-tight">To be used in only in the case of remittances to Bank through an officer of the zilla parishad</p>
                            <p className="font-medium leading-tight">जिल्हा परिषदेचे अंमलदारामार्फत बँकेत रकमा पाठविण्यांत येतील त्या बाबतीतच फक्त उपयोगात आणावयाचे.</p>
                        </div>
                    </div>

                    {/* Row 8: Totals Row */}
                    <div className="grid grid-cols-[3fr_1.5fr_2.5fr] border-t border-black min-h-[80px]">
                        <div className="p-4 border-r border-black flex flex-col justify-end gap-2 font-bold relative">
                            <div className="flex justify-between items-center w-full">
                                <span className="text-[14px]">Signature</span>
                                <span className="uppercase tracking-[0.2em] text-[15px]">Total</span>
                            </div>
                            <div className="flex justify-between items-center w-full">
                                <span className="text-[13px]">सही</span>
                                <span className="text-[14px]">एकूण</span>
                            </div>
                        </div>
                        <div className="border-r border-black flex items-center justify-center font-black text-[20px] bg-gray-50/20">
                            Rs - 250 /-
                        </div>
                        <div className="p-4"></div>
                    </div>

                    {/* Row 9: In Words Row */}
                    <div className="grid grid-cols-[3fr_4fr] border-t border-black">
                        <div className="p-4 border-r border-black font-bold">
                            <p className="text-[13px]">* In word Rupees - <span className="ml-[100px] text-[13px]">अक्षरी रुपये</span></p>
                        </div>
                        <div className="p-4 font-bold text-[14px]">
                            Rupees Two Hundred Fifty Only
                        </div>
                    </div>

                    {/* Row 10: Signatures Footer */}
                    <div className="grid grid-cols-[1fr_1fr] border-t border-black">
                        <div className="p-8 border-r border-black flex flex-col justify-end h-[160px]">
                            <p className="font-bold text-[15px]">Cashier</p>
                        </div>
                        <div className="flex flex-col h-[160px]">
                            <div className="grid grid-cols-2 border-b border-black h-1/2">
                                <div className="p-3 border-r border-black font-bold space-y-2">
                                    <p className="text-[11px]">Recevied Payment Rupees</p>
                                    <p className="text-gray-400">Date:   /   / 202</p>
                                </div>
                                <div className="p-3 font-bold">
                                    <p className="text-[11px]">In words Rupees</p>
                                </div>
                            </div>
                            <div className="p-3 flex items-end h-1/2">
                                <p className="font-bold text-[15px]">Agent</p>
                            </div>
                        </div>
                    </div>

                </div>

                {/* Pay Online Call to Action - Hidden on Print */}
                <div className="mt-12 flex justify-center print:hidden border-t pt-12 border-gray-50">
                    {isPaid ? (
                    <div className="bg-green-50/50 border-2 border-green-200 rounded-[2.5rem] p-10 flex flex-col items-center gap-6 max-w-md w-full shadow-xl shadow-green-100/20 animate-in fade-in slide-in-from-bottom-4 duration-700">
                        <div className="bg-green-100 text-green-600 w-24 h-24 rounded-full flex items-center justify-center shadow-lg border-4 border-white animate-pulse">
                        <CheckCircle2 size={48} />
                        </div>
                        <div className="text-center">
                        <h3 className="text-2xl font-black text-green-800 mb-2">पेमेंट यशस्वी झाले आहे!</h3>
                        <p className="text-green-700/70 font-bold text-sm">तुमचे वर्क सिलेक्शन शुल्क प्राप्त झाले आहे.</p>
                        </div>
                        <button 
                        onClick={() => setShowInvoice(true)}
                        className="w-full flex items-center justify-center gap-3 px-8 py-4 bg-green-600 hover:bg-green-700 text-white rounded-2xl font-black shadow-lg shadow-green-200 transition-all transform hover:-translate-y-1 active:scale-95 border-b-4 border-green-800"
                        >
                        <Printer size={20} />
                        पावती डाउनलोड करा (Download Invoice)
                        </button>
                    </div>
                    ) : (
                    <div className="text-center group">
                        <p className="text-gray-500 text-sm mb-6 font-medium italic animate-pulse">तुमचे पेमेंट पूर्ण करण्यासाठी खालील बटणावर क्लिक करा</p>
                        <button 
                        onClick={() => setShowPayment(true)}
                        className="flex items-center gap-4 px-12 py-5 bg-gradient-to-br from-blue-700 via-blue-800 to-indigo-900 text-white rounded-[1.25rem] hover:shadow-2xl hover:shadow-blue-200 transition-all transform hover:-translate-y-1 font-black text-xl shadow-xl border border-white/10"
                        >
                        <CreditCard size={28} />
                        ऑनलाइन पेमेंट करा (Pay Online)
                        </button>
                    </div>
                    )}
                </div>

                {/* Print Notice */}
                <div className="mt-12 text-center text-gray-400 font-bold uppercase tracking-widest text-[10px] print:hidden">
                    कंट्रोल + पी (Ctrl + P) दाबून चलन प्रिंट करा आणि बँकेत भरा.
                </div>
            </div>

            {/* Payment Modal */}
            {showPayment && (
                <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm z-[2000] flex items-center justify-center p-4 print:hidden">
                <div className="bg-white rounded-[3.5rem] w-full max-w-[550px] shadow-[0_40px_80px_-15px_rgba(0,0,0,0.5)] overflow-hidden animate-in fade-in zoom-in duration-500 border border-gray-100 flex flex-col relative">
                    
                    {/* Header Section - Blue Gradient */}
                    <div className="bg-gradient-to-b from-blue-700 to-blue-900 p-10 flex flex-col items-center gap-4 relative">
                    <button 
                        onClick={() => setShowPayment(false)}
                        className="absolute right-8 top-8 bg-white/20 hover:bg-white/30 p-2 rounded-full transition-all text-white shadow-lg backdrop-blur-md"
                        title="बंद करा"
                    >
                        <X size={20} />
                    </button>
                    
                    <h3 className="text-3xl font-bold text-white tracking-wide">यूपीआय पेमेंट</h3>
                    <div className="bg-[#2463EB] px-6 py-1 rounded-sm">
                        <p className="text-white text-[11px] font-black uppercase tracking-[0.2em]">Work Selection Payment</p>
                    </div>
                    </div>
                    
                    {/* Body Section */}
                    <div className="p-10 flex flex-col items-center gap-8">
                    {paymentDone ? (
                        <div className="py-12 flex flex-col items-center gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <div className="bg-green-50 text-green-500 w-24 h-24 rounded-full flex items-center justify-center shadow-xl shadow-green-100 border-4 border-white animate-bounce">
                            <CheckCircle2 size={48} />
                        </div>
                        <h4 className="text-2xl font-bold text-gray-900">यशस्वी!</h4>
                        <p className="text-gray-500 font-medium">पडताळणी प्रक्रियेत आहे...</p>
                        </div>
                    ) : (
                        <>
                        {/* QR Code with Floating Price Badge */}
                        <div className="relative group">
                            <div className="p-8 bg-white rounded-[2.5rem] shadow-[0_15px_50px_-10px_rgba(0,0,0,0.1)] border border-gray-100/50 relative">
                            <div className="absolute inset-0 bg-blue-400/10 blur-[30px] rounded-full -z-10 group-hover:bg-blue-400/20 transition-all duration-500"></div>
                            <img src={qrUrl} alt="UPI QR Code" className="w-[220px] h-[220px] rounded-xl" />
                            
                            {/* Floating Price Badge */}
                            <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-[#2D3383] text-white px-8 py-2 rounded-full font-black text-sm shadow-xl border-2 border-white min-w-[120px] text-center">
                                ₹250.00
                            </div>
                            </div>
                        </div>

                        {/* Input Section */}
                        <div className="w-full flex flex-col items-center gap-4 mt-2">
                            <div className="bg-[#2463EB] px-6 py-1 rounded-sm">
                            <label className="text-white text-[11px] font-black uppercase tracking-[0.1em]">तुमचा यूपीआय आयडी (YOUR UPI ID)</label>
                            </div>
                            <div className="w-full bg-[#f8f9fa] rounded-[2rem] p-4 border border-gray-100 shadow-inner group">
                            <input 
                                type="text" 
                                name="userUpiId"
                                value={paymentData.userUpiId}
                                onChange={handleInputChange}
                                placeholder="उदा. name@upi"
                                className="w-full bg-transparent text-center text-xl font-bold text-gray-800 placeholder:text-gray-300 outline-none"
                            />
                            </div>
                        </div>

                        {/* Footer Action */}
                        <div className="w-full flex flex-col gap-4">
                            <button 
                            onClick={handleCompletePayment}
                            disabled={loading}
                            className="w-full py-5 bg-gradient-to-r from-blue-700 to-indigo-900 text-white rounded-[2rem] font-black text-xl hover:shadow-[0_20px_40px_-5px_rgba(30,27,75,0.4)] transition-all flex items-center justify-center gap-3 disabled:opacity-50 transform active:scale-[0.98]"
                            >
                            {loading ? (
                                <div className="w-6 h-6 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
                            ) : (
                                <>पेमेंट पूर्ण करा</>
                            )}
                            </button>
                            <button 
                            onClick={() => setShowPayment(false)}
                            className="text-gray-400 font-bold text-xs uppercase tracking-widest hover:text-gray-600 transition-colors"
                            >
                            Go Back
                            </button>
                        </div>
                        </>
                    )}
                    </div>
                    
                    {/* Branding */}
                    {!paymentDone && (
                    <div className="px-10 pb-10 flex justify-center gap-10 opacity-20 filter grayscale">
                        <img src="https://upload.wikimedia.org/wikipedia/commons/e/e1/Google_Pay_Logo.svg" className="h-4" alt="GPay" />
                        <img src="https://upload.wikimedia.org/wikipedia/commons/7/71/PhonePe_Logo.svg" className="h-4" alt="PhonePe" />
                        <img src="https://upload.wikimedia.org/wikipedia/commons/2/24/Paytm_Logo_%28standalone%29.svg" className="h-5" alt="Paytm" />
                    </div>
                    )}
                </div>
                </div>
            )}

            {/* Invoice Overlay */}
            {showInvoice && invoiceData && (
                <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-md z-[3000] flex items-center justify-center p-4 overflow-y-auto print:p-0 print:bg-white print:backdrop-blur-none">
                <div className="bg-white rounded-[2.5rem] w-full max-w-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-500 flex flex-col print:shadow-none print:rounded-none">
                    
                    {/* Invoice Header */}
                    <div className="bg-gradient-to-r from-blue-800 to-indigo-900 p-8 text-white flex justify-between items-center print:from-white print:to-white print:text-black print:border-b-2 print:border-gray-800">
                    <div className="flex items-center gap-4">
                        <div className="bg-white/20 p-3 rounded-2xl backdrop-blur-md print:bg-gray-100 print:text-black">
                        <CreditCard size={32} />
                        </div>
                        <div>
                        <h2 className="text-2xl font-black uppercase tracking-tight">Payment Receipt</h2>
                        <p className="text-blue-100/70 text-xs font-bold print:text-gray-500">सांगली जिल्हा परिषद (Zilla Parishad Sangli)</p>
                        </div>
                    </div>
                    <div className="text-right print:hidden">
                        <button 
                        onClick={() => setShowInvoice(false)}
                        className="bg-white/10 hover:bg-white/20 p-2 rounded-full transition-colors"
                        >
                        <X size={20} />
                        </button>
                    </div>
                    </div>

                    <div className="p-10 space-y-8 bg-white print:bg-none">
                    
                    {/* Receipt Info Row */}
                    <div className="flex justify-between items-start border-b border-gray-100 pb-8 print:border-gray-800">
                        <div className="space-y-1">
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Receipt Number</p>
                        <p className="text-xl font-bold text-gray-800 tracking-tight">{invoiceData.receiptId}</p>
                        </div>
                        <div className="text-right space-y-1">
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Date & Time</p>
                        <p className="text-lg font-bold text-gray-800">
                            {new Date(invoiceData.date).toLocaleDateString('en-GB')} {new Date(invoiceData.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                        </div>
                    </div>

                    {/* Payer Details */}
                    <div className="grid grid-cols-2 gap-12 py-2">
                        <div className="space-y-2">
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Paid By (नाव)</p>
                        <p className="text-xl font-bold text-blue-900 leading-tight">{invoiceData.userName}</p>
                        <div className="flex items-center gap-2 text-gray-500">
                            <Hash size={14} />
                            <span className="text-sm font-medium">{invoiceData.mobile}</span>
                        </div>
                        </div>
                        <div className="text-right space-y-2">
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Payment Details</p>
                        <p className="text-lg font-bold text-gray-800">UPI Online Payment</p>
                        <p className="text-xs font-mono text-blue-600 bg-blue-50 px-3 py-1 rounded-full inline-block">{invoiceData.userUpiId}</p>
                        </div>
                    </div>

                    {/* Description Table */}
                    <div className="w-full bg-gray-50/50 rounded-[2rem] border border-gray-100 overflow-hidden print:border-gray-800">
                        <table className="w-full text-left border-collapse">
                        <thead className="bg-gray-100/50 print:bg-gray-200">
                            <tr>
                            <th className="px-8 py-4 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Description</th>
                            <th className="px-8 py-4 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] text-right">Amount</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 print:divide-gray-800">
                            <tr>
                            <td className="px-8 py-8">
                                <p className="font-bold text-gray-900 text-lg">वर्क सिलेक्शन फॉर्म फी</p>
                                <p className="text-xs text-gray-500 mt-1 font-medium italic">Selected Works: {workNumbersWithScheme}</p>
                                <p className="text-xs text-gray-500 font-medium italic">Work Selection Fee for ZP Sangli</p>
                            </td>
                            <td className="px-8 py-8 text-right">
                                <p className="text-2xl font-black text-gray-900">₹250.00</p>
                            </td>
                            </tr>
                            <tr className="bg-blue-600 print:bg-gray-100">
                            <td className="px-8 py-5 text-right">
                                <p className="text-[11px] font-black text-blue-100 uppercase tracking-widest print:text-black">Total Paid (एकूण भरले)</p>
                            </td>
                            <td className="px-8 py-5 text-right">
                                <p className="text-2xl font-black text-white print:text-black">₹250.00</p>
                            </td>
                            </tr>
                        </tbody>
                        </table>
                    </div>

                    {/* Status Stamp */}
                    <div className="flex justify-between items-center pt-4">
                        <div className="relative">
                        <div className="border-[3px] border-green-600 px-6 py-2 rounded-xl rotate-[-12deg] flex flex-col items-center shadow-lg shadow-green-100/50 print:shadow-none">
                            <span className="text-green-600 font-black text-2xl uppercase tracking-tighter">SUCCESSFUL</span>
                            <span className="text-green-600 text-[8px] font-bold uppercase tracking-widest">Zilla Parishad Sangli</span>
                        </div>
                        </div>
                        <div className="text-center space-y-4 print:mt-12">
                        <div className="h-[2px] w-48 bg-gray-200 mx-auto print:bg-gray-800"></div>
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Authorized Signatory</p>
                        </div>
                    </div>

                    {/* Footer Actions */}
                    <div className="flex gap-4 pt-6 print:hidden">
                        <button 
                        onClick={handlePrint}
                        className="flex-1 py-5 bg-gradient-to-r from-blue-700 to-indigo-900 text-white rounded-2xl font-black text-lg hover:shadow-2xl hover:shadow-blue-200 transition-all flex items-center justify-center gap-3 transform active:scale-[0.98]"
                        >
                        <Printer size={24} />
                        पावती प्रिंट करा (Print Receipt)
                        </button>
                        <button 
                        onClick={() => setShowInvoice(false)}
                        className="px-8 py-5 border-2 border-gray-100 text-gray-500 rounded-2xl font-bold hover:bg-gray-50 transition-colors"
                        >
                        बंद करा
                        </button>
                    </div>
                    </div>

                    {/* Note */}
                    <div className="px-10 py-6 bg-gray-50 border-t border-gray-100 text-center print:bg-white print:border-gray-800">
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-[0.2em]">This is a computer generated receipt. No physical signature is required.</p>
                    </div>
                </div>
                </div>
            )}

            <style dangerouslySetInnerHTML={{ __html: `
                @media print {
                    body { background: white !important; margin: 0 !important; padding: 0 !important; }
                    .max-w-[1000px] { max-width: 100% !important; border: none !important; margin: 0 !important; padding: 0 !important; }
                    .border-[1.5px] { border-width: 2px !important; }
                    .print\\:hidden { display: none !important; }
                }
            `}} />
        </div>
    );
};

export default WorkSelectionChallan;
